"use client";

import { useState, useEffect, useRef, useCallback, Fragment, type RefObject } from "react";
import type { OrbData } from "@/data/orbs";
import type { RingGeometryData } from "@/components/canvas/BootStarfield";
import type { Project } from "@/data/projects";
import type { ProtectedContent } from "@/components/ui/PasswordModal";
import { EphemeralText } from "@/components/ui/EphemeralText";
import { useLetterGlitch } from "@/hooks/useLetterGlitch";
import { Magnetic } from "@/components/ui/Magnetic";
import { track } from "@vercel/analytics";
import { LockIcon, UnlockIcon, Button, CloseIcon, ICON_SIZE_SM } from "./Button";
import { adjustColorLightness, getScrimColor } from "@/utils/colors";
import { CardScrim, SideNavArrows, CategoryPill } from "@/components/ui/card";
import { useFocusTrap } from "@/hooks/useFocusTrap";

// =============================================================================
// ORB CARD
// =============================================================================
// Unified card that morphs from idle preview to peek detail view
// Base content always visible, additional content fades in during peek

// Helper to render text with line breaks
function ResponsiveText({ children }: { children: string }) {
  return (
    <>
      {children.split("\n").map((segment, i, arr) => (
        <Fragment key={i}>
          {segment}
          {i < arr.length - 1 && <br />}
        </Fragment>
      ))}
    </>
  );
}

// Helper to detect if a tag value should be highlighted (numbers, proper nouns, ratings)
function isHighlightedTag(tag: string): boolean {
  return /\d|★|\$|%|[A-Z][a-z]+\s[A-Z]|^[A-Z][a-z]+$/.test(tag);
}

interface OrbCardProps {
  geometry: RingGeometryData | null;
  activeOrb: OrbData;
  isProfile: boolean;
  isPeeking: boolean;
  peekProgress: number; // 0-1 animation progress
  z3Height: number;
  scrollProgress?: number; // 0-1 scroll-driven morph progress (disables transitions when > 0)
  onPeekToggle: () => void;
  onClose: () => void;
  onRotateLeft?: () => void;
  onRotateRight?: () => void;
  orbHoveredSide?: "left" | "right" | null; // Which side of the orb is being hovered
  arrowKeyPressed?: "left" | "right" | null; // Which arrow key is currently pressed
  // Protected project props
  isProtected?: boolean;
  isUnlocked?: boolean;
  project?: Project; // Full project data for API call
  onPasswordSuccess?: (projectId: string, content: ProtectedContent) => void;
  // CRT effects
  crtEnabled?: boolean;
  // Theme
  isLightMode?: boolean;
  // Peek as a modal dialog: while true the card is role="dialog", focus moves in
  // and is trapped, and on close it returns to `returnFocusRef` (the orbit node).
  // Escape / ArrowUp close it via HomeShell's ring key handler.
  dialogOpen?: boolean;
  returnFocusRef?: RefObject<HTMLElement | null>;
  // Peek → read. Makes the "scroll to read" hint a real control, so the trapped
  // peek dialog has a keyboard / screen-reader way into the case study.
  onReadRequest?: () => void;
}

const CARD_FADE_DELAY_MS = 0;
const CARD_FADE_DURATION_MS = 400;

export function OrbCard({
  geometry,
  activeOrb,
  isProfile,
  peekProgress,
  z3Height,
  scrollProgress = 0,
  onPeekToggle,
  onClose,
  onRotateLeft,
  onRotateRight,
  orbHoveredSide,
  arrowKeyPressed,
  isProtected = false,
  isUnlocked = false,
  project,
  onPasswordSuccess,
  crtEnabled = true,
  isLightMode = false,
  dialogOpen = false,
  returnFocusRef,
  onReadRequest,
}: OrbCardProps) {
  const scrimColor = getScrimColor(isLightMode);
  const cardRef = useRef<HTMLDivElement>(null);
  useFocusTrap(cardRef, dialogOpen, returnFocusRef);
  const [hasEntered, setHasEntered] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Password state for protected projects
  const [password, setPassword] = useState("");
  const [pwError, setPwError] = useState(false);
  const [pwShaking, setPwShaking] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwErrorMessage, setPwErrorMessage] = useState("");
  const [cooldownSec, setCooldownSec] = useState(0);
  const [pwFocused, setPwFocused] = useState(false);
  const [pwButtonHover, setPwButtonHover] = useState(false);
  const pwInputRef = useRef<HTMLInputElement>(null);

  // Countdown timer for cooldown
  useEffect(() => {
    if (cooldownSec <= 0) return;
    const timer = setTimeout(() => {
      setCooldownSec((s) => {
        const next = s - 1;
        if (next <= 0) {
          setPwError(false);
          setPwErrorMessage("");
        }
        return next;
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldownSec]);

  // Reset password state when switching orbs
  useEffect(() => {
    setPassword("");
    setPwError(false);
    setPwErrorMessage("");
    setCooldownSec(0);
  }, [activeOrb.id]);

  // Autofocus password input when entering peek for protected projects
  const prevPeekProgressRef = useRef(peekProgress);
  useEffect(() => {
    const wasBelow = prevPeekProgressRef.current < 0.5;
    const isAbove = peekProgress >= 0.5;
    prevPeekProgressRef.current = peekProgress;

    // Focus when crossing threshold into peek (0.5 is when form is interactive)
    if (wasBelow && isAbove && isProtected && !isUnlocked) {
      // Small delay to ensure DOM is ready
      setTimeout(() => pwInputRef.current?.focus(), 50);
    }
  }, [peekProgress, isProtected, isUnlocked]);

  const handlePasswordSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwLoading || cooldownSec > 0 || !project) return;

    setPwLoading(true);
    setPwError(false);
    setPwErrorMessage("");

    try {
      const response = await fetch(`/api/projects/${project.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: password.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        track("password_unlock", { project: project.id, success: true });
        onPasswordSuccess?.(project.id, data.content);
      } else {
        track("password_unlock", { project: project.id, success: false });
        setPwError(true);
        setPwShaking(true);
        setTimeout(() => setPwShaking(false), 500);
        setPassword("");

        if (data.retryAfterMs && data.retryAfterMs > 0) {
          setCooldownSec(Math.ceil(data.retryAfterMs / 1000));
        }
        setPwErrorMessage(data.error || "Incorrect password");
      }
    } catch {
      setPwError(true);
      setPwShaking(true);
      setTimeout(() => setPwShaking(false), 500);
      setPwErrorMessage("Something went wrong");
    } finally {
      setPwLoading(false);
    }
  }, [password, pwLoading, cooldownSec, project, onPasswordSuccess]);

  // Letter glitch for headline (CRT aesthetic, disabled when CRT is off)
  const { text: glitchedHeadline } = useLetterGlitch(activeOrb.headline, { idleGlitch: true, enabled: crtEnabled });

  // Detect touch device (no hover capability)
  useEffect(() => {
    setIsTouchDevice(window.matchMedia("(hover: none)").matches);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHasEntered(true);
    }, CARD_FADE_DELAY_MS + CARD_FADE_DURATION_MS);
    return () => clearTimeout(timer);
  }, []);

  const viewportWidth = geometry?.viewportWidth ?? (typeof window !== "undefined" ? window.innerWidth : 400);
  const idleCardWidth = Math.min(560, viewportWidth * 0.92);

  // In peek mode, go full width
  const cardWidth = idleCardWidth + (viewportWidth - idleCardWidth) * peekProgress;
  const cardLeft = ((viewportWidth - idleCardWidth) / 2) * (1 - peekProgress);

  // Animation values based on peekProgress
  const idleBottom = 28; // Idle position (closer to Z3 edge)
  const peekBottom = 0; // Peek position (anchored to bottom)
  const currentBottom = idleBottom + (peekBottom - idleBottom) * peekProgress;

  // Opacity for progressive reveal
  const baseOpacity = hasEntered ? 1 : 0;
  const animation = hasEntered ? undefined : `orbCardFadeIn ${CARD_FADE_DURATION_MS}ms ease-out ${CARD_FADE_DELAY_MS}ms forwards`;

  // Progressive content reveal (eyebrow, tags appear as peek progresses)
  const additionalContentOpacity = Math.max(0, (peekProgress - 0.3) / 0.7); // Starts at 30%, full at 100%
  const glassOpacity = peekProgress;

  // Interpolate sizes based on peekProgress
  const categorySize = 16 + peekProgress * 2; // 16 -> 18
  const categoryPadding = `${4 + peekProgress * 2}px ${12 + peekProgress * 4}px`; // 4/12 -> 6/16
  // Headline: 18px idle -> clamp(24px, 6vw, 32px) in peek (responsive, max 2 lines)
  const headlineSize = peekProgress < 0.1
    ? "22px"
    : `clamp(${22 + peekProgress * 4}px, ${peekProgress * 6}vw, ${22 + peekProgress * 10}px)`;
  const subheadSize = 18 + peekProgress * 2; // 18 -> 20

  return (
    <>
      <style jsx>{`
        @keyframes orbCardFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Shared scrim gradient (idle + peek) */}
      <CardScrim
        scrimColor={scrimColor}
        hasEntered={hasEntered}
        animation={animation}
        peekProgress={peekProgress}
        idleScrimHeight={380}
      />

      {/* Opaque backdrop for peek (covers canvas) - DISABLED for testing */}
      {/* <div
        className="fixed z-[99] pointer-events-none"
        style={{
          bottom: 0,
          left: 0,
          right: 0,
          height: z3Height,
          backgroundColor: "rgb(10, 10, 10)",
          opacity: peekProgress,
          transition: "opacity 0.3s ease",
        }}
      /> */}

      {/* Main card container */}
      <div
        ref={cardRef}
        role={dialogOpen ? "dialog" : undefined}
        aria-modal={dialogOpen ? true : undefined}
        aria-label={dialogOpen ? `${activeOrb.headline.replace(/\s*\n\s*/g, " ")} details` : undefined}
        data-orbit-peek={dialogOpen ? "" : undefined}
        className="fixed z-[100]"
        style={{
          bottom: currentBottom,
          left: cardLeft,
          width: cardWidth,
          minHeight: peekProgress > 0.1 ? z3Height * peekProgress : undefined,
          opacity: baseOpacity,
          animation,
          pointerEvents: "none",
          // Disable transitions during scroll-driven morph for instant response
          transition: hasEntered && scrollProgress === 0 ? "bottom 0.3s ease, min-height 0.3s ease, left 0.3s ease, width 0.3s ease" : undefined,
        }}
      >
        {/* Glass background - fades in during peek */}
        <div
          className="absolute inset-0 rounded-t-xl"
          style={{
            opacity: glassOpacity,
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            backgroundColor: "rgba(var(--foreground-rgb),0.04)",
            border: "1px solid rgba(var(--foreground-rgb),0.08)",
            borderBottom: "none",
            transition: "opacity 0.3s ease",
          }}
        />

        {/* Protected content scrim - dims background behind password modal */}
        {isProtected && !isUnlocked && peekProgress > 0.5 && (
          <div
            className="absolute left-0 right-0 bottom-0 rounded-t-xl z-[1] pointer-events-none"
            style={{
              height: 180, // Just above PROTECTED text
              background: `linear-gradient(to top, rgba(${scrimColor},0.9) 0%, rgba(${scrimColor},0.85) 70%, rgba(${scrimColor},0) 100%)`,
              opacity: additionalContentOpacity,
              transition: "opacity 0.3s ease",
            }}
          />
        )}

        {/* Shared side navigation + center content */}
        {(() => {
          const showSideNav = !isProfile && peekProgress < 0.3 && !!onRotateLeft && !!onRotateRight;

          return (
            <SideNavArrows
              onRotateLeft={onRotateLeft ?? (() => {})}
              onRotateRight={onRotateRight ?? (() => {})}
              orbHoveredSide={orbHoveredSide}
              arrowKeyPressed={arrowKeyPressed}
              accentColor={activeOrb.color}
              isLightMode={isLightMode}
              visible={showSideNav}
              responsive
              leftLabel="Previous project"
              rightLabel="Next project"
            >
              <Magnetic strength={peekProgress < 0.1 ? 0.06 : 0} tiltStrength={peekProgress < 0.1 ? 3 : 0} padding={100}>
                <div
                  className="relative flex flex-col items-center justify-end text-center py-6 cursor-pointer"
                  style={{
                    minHeight: peekProgress < 0.1 ? 180 : undefined,
                  }}
                  onClick={peekProgress < 0.5 ? onPeekToggle : undefined}
                  onMouseEnter={() => peekProgress < 0.5 && setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {/* Close button - circle X at top, appears during peek */}
                  {peekProgress > 0.3 && (
                    <div
                      className="absolute top-0 left-0 right-0 flex items-center justify-center py-4"
                      style={{
                        opacity: additionalContentOpacity,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <Button
                        variant="icon-sm"
                        magnetic={false}
                        className="text-[var(--toggle-icon)] !bg-transparent !backdrop-blur-none hover:!bg-[rgba(var(--surface-rgb),0.6)] hover:!backdrop-blur-xl transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                        aria-label="Close details"
                      >
                        <CloseIcon size={ICON_SIZE_SM} />
                      </Button>
                    </div>
                  )}

                  {/* Spacer for close ribbon */}
                  {peekProgress > 0.3 && <div style={{ height: 32 }} />}

          {/* Eyebrow - progressive reveal */}
          {activeOrb.eyebrow && !isProfile && additionalContentOpacity > 0 && (
            <p
              className="mb-3 text-center"
              style={{
                fontSize: 11,
                fontWeight: 900,
                opacity: 0.5 * additionalContentOpacity,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: isLightMode ? "rgba(0, 0, 0, 0.85)" : "rgba(255, 255, 255, 0.85)",
                transform: `translateY(${(1 - additionalContentOpacity) * -8}px)`,
                transition: "opacity 0.3s ease, transform 0.3s ease",
              }}
            >
              {activeOrb.eyebrow}
            </p>
          )}

          {/* Shared category pill */}
          {activeOrb.category && !isProfile && (
            <CategoryPill
              label={activeOrb.category}
              color={activeOrb.color}
              filled={(isHovered && peekProgress < 0.5) || isTouchDevice || peekProgress > 0.3}
              fontSize={categorySize}
              padding={categoryPadding}
              marginBottom={10 + peekProgress * 6}
            />
          )}

          {/* Headline */}
          <span
            className="font-bold font-headline leading-tight text-balance"
            style={{
              fontSize: headlineSize,
              marginBottom: 4 + peekProgress * 4,
              color: isLightMode ? "rgba(0, 0, 0, 0.95)" : "rgba(255, 255, 255, 0.95)",
              transition: "font-size 0.3s ease, margin 0.3s ease, color 0.3s ease",
              transform: crtEnabled ? 'perspective(400px) rotateX(10deg)' : 'none',
              transformOrigin: 'bottom center',
              textShadow: crtEnabled ? 'var(--crt-text-shadow)' : 'none',
            }}
          >
            <EphemeralText hoverOpacity={0.5}>{glitchedHeadline}</EphemeralText>
          </span>

          {/* Subhead */}
          <span
            className="leading-relaxed text-balance"
            style={{
              fontSize: subheadSize,
              marginTop: 6 - peekProgress * 6,
              marginBottom: peekProgress * 16,
              color: isLightMode ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.55)",
              transition: "font-size 0.3s ease, margin 0.3s ease",
            }}
          >
            <ResponsiveText>{activeOrb.subhead}</ResponsiveText>
          </span>

          {/* Down triangle hint - fades out during peek, brightens on hover */}
          {!isProfile && (1 - peekProgress) > 0.1 && (
            <div
              style={{
                marginTop: 12,
                opacity: isHovered ? (1 - peekProgress) * 0.9 : (1 - peekProgress) * 0.4,
                transition: "opacity 0.2s ease",
              }}
            >
              <div style={{
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: `8px solid ${isHovered ? activeOrb.color : (isLightMode ? "rgba(0, 0, 0, 0.5)" : "rgba(255, 255, 255, 0.7)")}`,
                transition: "border-color 0.2s ease",
              }} />
            </div>
          )}

          {/* Tags - progressive reveal */}
          {activeOrb.tags && activeOrb.tags.length > 0 && !isProfile && additionalContentOpacity > 0 && (
            <div
              className="flex flex-wrap gap-2.5 justify-center"
              style={{
                marginBottom: 16,
                opacity: additionalContentOpacity,
                transform: `translateY(${(1 - additionalContentOpacity) * 12}px)`,
                transition: "opacity 0.3s ease, transform 0.3s ease",
              }}
            >
              {activeOrb.tags.map((tag, i) => {
                // Check if this is a role tag (Designer, Lead, etc.)
                const isRoleTag = /designer|lead/i.test(tag);

                if (isRoleTag) {
                  // Role tags: brand-colored glass fill with shimmer text
                  const baseColor = activeOrb.color;
                  const brightColor = adjustColorLightness(activeOrb.color, 0.25);
                  const darkerColor = adjustColorLightness(activeOrb.color, -0.15);

                  return (
                    <div
                      key={i}
                      className="px-3 py-1.5 rounded-full text-[0.75rem] font-bold"
                      style={{
                        backgroundColor: isLightMode ? `${activeOrb.color}35` : `${activeOrb.color}20`,
                        border: `1.5px solid ${isLightMode ? activeOrb.color : `${activeOrb.color}60`}`,
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        boxShadow: `inset 0 0 12px ${activeOrb.color}15`,
                      }}
                    >
                      <span
                        className="animate-gradient-x"
                        style={{
                          backgroundImage: isLightMode
                            ? `linear-gradient(90deg, ${darkerColor}, ${baseColor}, ${darkerColor})`
                            : `linear-gradient(90deg, ${baseColor}, ${brightColor}, ${baseColor})`,
                          backgroundSize: "200% 100%",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        {tag}
                      </span>
                    </div>
                  );
                }

                // Metric tags: split into highlighted value + neutral label
                // Matches: $X+, XM+, XK+, X%, X★, Xx, plain numbers, Day X, ordinals, brand words, I/O years
                const match = tag.match(/^(\$[\d.]+[KMB]?\+?|[\d.]+[KMB]+\+?|[\d.]+%|[\d.]+★|[\d.]+x|[\d.]+\+?|Day \d+|\d+(?:st|nd|rd|th)|Google I\/O \d{4}|I\/O \d{4}|Amazon \d{4}|Google|Amazon|Featured)\s*/i);
                const highlighted = match ? match[1] : null;
                const rest = match ? tag.slice(match[0].length) : tag;

                return (
                  <div
                    key={i}
                    className="px-3 py-1.5 rounded-full text-[0.75rem] font-bold"
                    style={{
                      backgroundColor: isLightMode ? "rgba(0, 0, 0, 0.06)" : "rgba(255, 255, 255, 0.05)",
                      border: isLightMode ? "1.5px solid rgba(0, 0, 0, 0.1)" : "1.5px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    {highlighted && (
                      <span style={{ color: activeOrb.color }}>{highlighted}</span>
                    )}
                    {rest && (
                      <span style={{ color: isLightMode ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.6)" }}>{highlighted ? ` ${rest}` : rest}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

                </div>
              </Magnetic>
            </SideNavArrows>
          );
        })()}

        {/* Bottom action area - Password input OR scroll hint */}
        {additionalContentOpacity > 0.5 && (
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-[2]"
            style={{
              opacity: additionalContentOpacity,
            }}
            onClick={(e) => {
              // Stop propagation for protected projects to prevent closing peek
              if (isProtected && !isUnlocked) {
                e.stopPropagation();
              }
            }}
            onMouseDown={(e) => {
              if (isProtected && !isUnlocked) {
                e.stopPropagation();
              }
            }}
          >
            {isProtected && !isUnlocked ? (
              /* Password input for protected projects - matches PasswordModal design */
              <form
                onSubmit={handlePasswordSubmit}
                className="flex flex-col items-center gap-3 w-full max-w-[280px]"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Protected indicator */}
                <span
                  className="text-xs uppercase tracking-[0.15em] font-black"
                  style={{ color: activeOrb.color }}
                >
                  Protected
                </span>

                {/* Pill-shaped input with embedded button */}
                <div
                  className="flex items-center rounded-full overflow-hidden w-full"
                  style={{
                    backgroundColor: "rgba(20,20,20,0.85)",
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                    border: `1px solid ${
                      pwError
                        ? "rgba(255,80,80,0.5)"
                        : pwFocused
                        ? `${activeOrb.color}80`
                        : "rgba(255,255,255,0.12)"
                    }`,
                    boxShadow: pwFocused && !pwError
                      ? `0 0 0 3px ${activeOrb.color}26`
                      : "none",
                    transition: "all 0.2s ease",
                    animation: pwShaking ? "shake 0.5s ease-in-out" : undefined,
                  }}
                >
                  <input
                    ref={pwInputRef}
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPwError(false);
                    }}
                    onFocus={() => setPwFocused(true)}
                    onBlur={() => setPwFocused(false)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    placeholder="Enter password"
                    disabled={pwLoading || cooldownSec > 0}
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    className="flex-1 pl-4 pr-2 py-3 bg-transparent text-white text-sm placeholder:text-white/40"
                    style={{ outline: "none" }}
                  />
                  <button
                    type="submit"
                    disabled={pwLoading || cooldownSec > 0}
                    onMouseEnter={() => setPwButtonHover(true)}
                    onMouseLeave={() => setPwButtonHover(false)}
                    className="h-8 px-4 mr-1.5 rounded-full text-sm font-medium flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait"
                    style={{
                      backgroundColor: (password.trim() || pwButtonHover)
                        ? activeOrb.color
                        : `${activeOrb.color}40`,
                      color: (password.trim() || pwButtonHover) ? "#000" : activeOrb.color,
                      cursor: password.trim() ? "pointer" : "default",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {pwLoading ? (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        className="animate-spin"
                      >
                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <span
                        style={{
                          display: "flex",
                          transform: password.trim() ? "scale(1.15)" : "scale(1)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        {password.trim() ? <UnlockIcon size={14} /> : <LockIcon size={14} />}
                      </span>
                    )}
                  </button>
                </div>

                {/* Error message OR hint */}
                {(pwError || cooldownSec > 0) ? (
                  <p
                    className="text-center"
                    style={{ fontSize: 11, color: "rgba(255,80,80,0.9)" }}
                  >
                    {cooldownSec > 0
                      ? `Wait ${cooldownSec}s before trying again`
                      : pwErrorMessage || "Incorrect password"}
                  </p>
                ) : project?.passwordHint ? (
                  <p
                    className="text-center italic text-sm"
                    style={{ color: "rgba(var(--foreground-rgb),0.4)" }}
                  >
                    Hint: {project.passwordHint}
                  </p>
                ) : null}
              </form>
            ) : (
              /* Scroll to read hint for unlocked projects. Same look as before; it is
                 a button so keyboard and screen-reader users have a way to read too. */
              <button
                type="button"
                onClick={onReadRequest}
                disabled={!onReadRequest}
                aria-label="Scroll to read: open the case study"
                className="flex flex-col items-center gap-2 bg-transparent border-0 p-1 rounded-md"
                style={{ pointerEvents: "auto" }}
              >
                <span
                  className="text-center"
                  style={{
                    fontSize: 10,
                    color: isLightMode ? "rgba(0, 0, 0, 0.4)" : "rgba(255, 255, 255, 0.4)",
                  }}
                >
                  scroll to read
                </span>
                <span aria-hidden="true" style={{
                  display: "block",
                  width: 0,
                  height: 0,
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderTop: isLightMode ? "8px solid rgba(0, 0, 0, 0.25)" : "8px solid rgba(255, 255, 255, 0.3)",
                }} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Shake animation for password error */}
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-8px); }
          80% { transform: translateX(8px); }
        }
      `}</style>
    </>
  );
}
